import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { NewsArticle } from '../../../mocks/news';
import { getNewsList, categoryBadgeClass } from '../../../services/news';

export default function NewsSection() {
  const { t, i18n } = useTranslation();
  const [displayNews, setDisplayNews] = useState<NewsArticle[]>([]);

  useEffect(() => {
    let mounted = true;

    async function loadNews() {
      const result = await getNewsList({
        page: 1,
        pageSize: 3,
        category: 'all',
        locale: i18n.resolvedLanguage ?? i18n.language,
      });
      if (mounted) {
        setDisplayNews(result.items);
      }
    }

    void loadNews();
    return () => {
      mounted = false;
    };
  }, [i18n.resolvedLanguage, i18n.language]);

  return (
    <section className="bg-white py-24 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 bg-accent/10 border border-accent/20 rounded-full">
              <i className="ri-newspaper-line text-accent text-sm" />
              <span className="text-accent text-xs font-medium uppercase tracking-wider">{t('news.tag')}</span>
            </div>
            <h2 className="text-4xl xl:text-5xl font-heading font-bold text-gray-900 mb-3">{t('news.title')}</h2>
            <p className="text-gray-500 text-base">{t('news.subtitle')}</p>
          </div>
          <Link to="/news" className="inline-flex items-center gap-2 text-accent text-sm font-medium hover:gap-3 transition-all duration-200 whitespace-nowrap cursor-pointer">
            {t('news.more')} <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {displayNews.map((news) => (
            <Link key={news.id} to={`/news/${news.id}`} className="group bg-white border border-primary-100 rounded-2xl overflow-hidden card-glow transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="relative w-full h-48 overflow-hidden">
                <img src={news.image} alt={news.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent" />
                <div className="absolute top-4 left-4">
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full border ${categoryBadgeClass(news.category)}`}
                  >
                    {news.categoryLabel}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-1.5 text-gray-400 text-xs mb-3">
                  <i className="ri-calendar-line" />{news.date}
                </div>
                <h3 className="text-base font-bold text-gray-900 leading-snug mb-3 group-hover:text-primary transition-colors duration-200 line-clamp-2">{news.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-4">{news.summary}</p>
                <div className="flex items-center gap-1.5 text-primary text-xs font-medium">
                  {t('news.read')} <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
